import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, InfoIcon, MessageCircleIcon, MinusIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';
import { Overline } from '../Overline';
import { LeadForm } from './LeadForm';
import { OptionButton } from './OptionButton';
import { FLOOR_OPTIONS, SKIRTING_OPTIONS, useCalculator, DOOR_PRICE } from './useCalculator';
import { WHATSAPP_URL } from '../../data/content';
const TOTAL_STEPS = 8;
const euro = (value: number) => new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
}).format(value);
const stepTitles = ['¿Qué tipo de suelo quieres?', '¿Qué tipo de rodapiés quieres?', '¿Cuántos metros cuadrados quieres instalar?', '¿Quieres mano de obra o solo el material?', '¿Hay muebles que mover o la casa está vacía?', '¿Hay un suelo antiguo para retirar?', '¿La superficie está nivelada?', '¿Hay puertas que requieran ajuste?'];
export function Calculator() {
  const {
    state,
    set,
    reset,
    result
  } = useCalculator();
  const [step, setStep] = useState(1);
  const [showResult, setShowResult] = useState(false);
  const next = () => {
    if (step === TOTAL_STEPS) {
      setShowResult(true);
      return;
    }
    setStep(s => Math.min(TOTAL_STEPS, s + 1));
  };
  const back = () => setStep(s => Math.max(1, s - 1));
  const restart = () => {
    reset();
    setStep(1);
    setShowResult(false);
  };
  return <section id="calculadora" className="bg-sand py-24 lg:py-32" data-id="element-20" data-mp="352g150uok31h">
      <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-21" data-mp="304eey71yjnls">
        <div className="max-w-3xl" data-id="element-22" data-mp="304oaea5rumyv">
          <Overline data-id="element-23" data-mp="2v5bfd24qy9l7">Calculadora Suelo Perfecto™</Overline>
          <h2 className="mt-6 font-serif text-[2.1rem] font-light leading-[1.1] text-ink sm:text-[2.8rem]" data-id="element-24" data-mp="2v6w9wrr29sj2">
            Calcula el precio aproximado de tu nuevo suelo
          </h2>
          <p className="mt-6 text-[15px] leading-relaxed text-stone-600" data-id="element-25" data-mp="tf9nz4m10ntg">
            Responde unas preguntas rápidas y obtén una estimación orientativa. Después revisamos
            medidas, materiales y acabados para darte un presupuesto cerrado.
          </p>
        </div>

        <div className="mt-14 overflow-hidden rounded-[28px] border border-ink/10 bg-white shadow-lift" data-id="element-26" data-mp="2dws9zhya082r">
          {!showResult ? <div className="grid lg:grid-cols-12" data-id="element-27" data-mp="2ivlmp85qnv1v">
              <div className="border-b border-ink/10 bg-cream p-8 lg:col-span-4 lg:border-b-0 lg:border-r lg:p-10" data-id="element-28" data-mp="36kxy8eojgf7c">
                <p className="text-[11px] uppercase tracking-overline text-walnut" data-id="element-29" data-mp="3bk139z4iqlfb">
                  Paso {step} de {TOTAL_STEPS}
                </p>
                <h3 className="mt-5 font-serif text-[1.6rem] font-light leading-snug text-ink" data-id="element-30" data-mp="2wol6vnggudfr">
                  {stepTitles[step - 1]}
                </h3>
                <div className="mt-8 h-px w-full bg-ink/10" data-id="element-31" data-mp="1l6accwgbmywq">
                  <div className="h-px bg-walnut transition-[width] duration-300 ease-premium" style={{
                width: `${step / TOTAL_STEPS * 100}%`
              }} data-id="element-32" data-mp="1q66eeq5gkg9n" />
                </div>
                <dl className="mt-8 space-y-3 text-[12.5px] text-stone-600" data-id="element-33" data-mp="38nco8valcow">
                  <div className="flex justify-between gap-4" data-id="element-34" data-mp="h97zrh88puo7">
                    <dt data-id="element-35" data-mp="h7dkojjngws1">Superficie</dt>
                    <dd className="text-ink" data-id="element-36" data-mp="d7cy2bmedf9w">{state.meters} m²</dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-37" data-mp="3lng3sbty8tvl">
                    <dt data-id="element-38" data-mp="3llligwermzqz">Material</dt>
                    <dd className="text-ink" data-id="element-39" data-mp="3gov0g3jehu2x">
                      {FLOOR_OPTIONS.find(f => f.id === state.floor)?.label}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4" data-id="element-40" data-mp="3uhuiodaki2e6">
                    <dt data-id="element-41" data-mp="3pohw08pg85o2">Servicio</dt>
                    <dd className="text-ink" data-id="element-42" data-mp="3pngg9qxhmv00">
                      {state.service === 'completo' ? 'Completo' : 'Solo material'}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="p-8 lg:col-span-8 lg:p-10" data-id="element-43" data-mp="3anmh4n3okiod">
                <AnimatePresence mode="wait" data-id="element-44" data-mp="3ap5v271v6hmz">
                  <motion.div key={step} initial={{
                opacity: 0,
                y: 12
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -8
              }} transition={{
                duration: 0.22,
                ease: [0.23, 1, 0.32, 1]
              }} data-id="element-45" data-mp="3flwaqt9rbb7v">
                    {step === 1 && <div className="grid gap-4 sm:grid-cols-3" data-id="element-46" data-mp="3ub29yh8lh1fr">
                        {FLOOR_OPTIONS.map(option => <OptionButton key={option.id} label={option.label} detail={option.detail} hint={`desde ${option.price} €/m²`} selected={state.floor === option.id} onClick={() => set('floor', option.id)} data-id="element-47" data-mp="76me40omyy7h" />)}
                      </div>}

                    {step === 2 && <div className="grid gap-4 sm:grid-cols-2" data-id="element-48" data-mp="21maywmreb8hf">
                        {SKIRTING_OPTIONS.map(option => <OptionButton key={option.id} label={option.label} detail={option.detail} hint={`${option.price} € / metro lineal`} selected={state.skirting === option.id} onClick={() => set('skirting', option.id)} data-id="element-49" data-mp="1wojba4xsa8j1" />)}
                      </div>}

                    {step === 3 && <div className="rounded-2xl border border-ink/15 bg-white p-7" data-id="element-50" data-mp="2ahin9wycxklu">
                        <div className="flex items-end justify-between" data-id="element-51" data-mp="140dl32nynrbt">
                          <span className="font-serif text-5xl font-light text-ink" data-id="element-52" data-mp="yz7v3cloqi5z">
                            {state.meters}
                            <span className="ml-2 text-xl text-stone-600" data-id="element-53" data-mp="3n3tts6rbt73a">m²</span>
                          </span>
                          <span className="text-[12px] text-stone-600" data-id="element-54" data-mp="3d7qur9rrtokk">
                            ≈ {state.meters} ml de rodapié
                          </span>
                        </div>
                        <label className="sr-only" htmlFor="meters-range" data-id="element-55" data-mp="3i8ocxmcs6im2">
                          Metros cuadrados a instalar
                        </label>
                        <input id="meters-range" type="range" min={5} max={299} step={1} value={state.meters} onChange={e => set('meters', Number(e.target.value))} className="mt-8 w-full cursor-pointer" data-id="element-56" data-mp="389gy0uqeefwu" />
                        <div className="mt-3 flex justify-between text-[11px] uppercase tracking-overline text-stone-400" data-id="element-57" data-mp="2pk0jv4lsioxy">
                          <span data-id="element-58" data-mp="21varei3c8bvp">5 m²</span>
                          <span data-id="element-59" data-mp="21wdior4kogc5">299 m²</span>
                        </div>
                      </div>}

                    {step === 4 && <div className="grid gap-4 sm:grid-cols-2" data-id="element-60" data-mp="2auxquycib7m4">
                        <OptionButton label="Servicio completo" detail="Instalación profesional, rodapiés, remates y entrega limpia." selected={state.service === 'completo'} onClick={() => set('service', 'completo')} data-id="element-61" data-mp="2axtriufqoae9" />
                        <OptionButton label="Solo material" detail="Te suministramos el material y tú gestionas la instalación." selected={state.service === 'material'} onClick={() => set('service', 'material')} data-id="element-62" data-mp="2tn03zzpxw5rm" />
                      </div>}

                    {step === 5 && <div className="grid gap-4 sm:grid-cols-2" data-id="element-63" data-mp="1t52arxfary67">
                        <OptionButton label="Sí, hay muebles" detail="Los movemos y protegemos antes de empezar." selected={state.furniture === 'si'} onClick={() => set('furniture', 'si')} data-id="element-64" data-mp="1y71dd537qr23" />
                        <OptionButton label="No, está vacía" detail="La vivienda está libre para trabajar." selected={state.furniture === 'no'} onClick={() => set('furniture', 'no')} data-id="element-65" data-mp="1agx9cwm44pak" />
                      </div>}

                    {step === 6 && <div className="grid gap-4 sm:grid-cols-2" data-id="element-66" data-mp="rocy0t2gj2ui">
                        <OptionButton label="Sí, hay que retirarlo" detail="Retiramos el pavimento antiguo y gestionamos escombros." selected={state.removal === 'si'} onClick={() => set('removal', 'si')} data-id="element-67" data-mp="350h1hdha19y3" />
                        <OptionButton label="No" detail="Se instala sobre el soporte actual." selected={state.removal === 'no'} onClick={() => set('removal', 'no')} data-id="element-68" data-mp="2qzwlep2qxs8a" />
                      </div>}

                    {step === 7 && <div className="grid gap-4 sm:grid-cols-3" data-id="element-69" data-mp="28b80420cpkvz">
                        <OptionButton label="Está apta" detail="Superficie nivelada, lista para instalar." selected={state.leveling === 'apto'} onClick={() => set('leveling', 'apto')} data-id="element-70" data-mp="23bda00oegzbz" />
                        <OptionButton label="Hay que autonivelar" detail="Existen desniveles visibles en el soporte." selected={state.leveling === 'autonivelar'} onClick={() => set('leveling', 'autonivelar')} data-id="element-71" data-mp="3sbhp88s3lqrc" />
                        <OptionButton label="No lo sé" detail="Necesito asesoramiento técnico." selected={state.leveling === 'no-lo-se'} onClick={() => set('leveling', 'no-lo-se')} data-id="element-72" data-mp="3eb6xz4mvma5n" />
                      </div>}

                    {step === 8 && <div className="rounded-2xl border border-ink/15 bg-white p-7" data-id="element-73" data-mp="12jjiz3u67ayn">
                        <div className="flex items-center justify-between gap-6" data-id="element-74" data-mp="xlrpw6hshisv">
                          <div data-id="element-75" data-mp="1628shlqpxrxv">
                            <span className="font-serif text-5xl font-light text-ink" data-id="element-76" data-mp="165ekp9un99xt">
                              {state.doors}
                            </span>
                            <p className="mt-2 text-[12.5px] text-stone-600" data-id="element-77" data-mp="w65qqgzs3vbg">
                              {state.doors === 0 ? 'Ninguna puerta a ajustar' : `${DOOR_PRICE} € por puerta ajustada`}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-78" data-mp="1ts35q4ey9iin">
                            <button type="button" aria-label="Quitar una puerta" onClick={() => set('doors', Math.max(0, state.doors - 1))} className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-200 ease-premium hover:border-ink hover:bg-sand/50 disabled:opacity-30" disabled={state.doors === 0} data-id="element-79" data-mp="1f2yoapfzbibl">
                              <MinusIcon className="h-4 w-4" data-id="element-80" data-mp="2hegkipyog7dn" />
                            </button>
                            <button type="button" aria-label="Añadir una puerta" onClick={() => set('doors', Math.min(20, state.doors + 1))} className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors duration-200 ease-premium hover:border-ink hover:bg-sand/50 disabled:opacity-30" disabled={state.doors === 20} data-id="element-81" data-mp="2cj1904o56nke">
                              <PlusIcon className="h-4 w-4" data-id="element-82" data-mp="1kqacru6gkyv5" />
                            </button>
                          </div>
                        </div>
                        <label className="sr-only" htmlFor="doors-range" data-id="element-83" data-mp="1fta5xh1sbs2z">
                          Número de puertas a ajustar
                        </label>
                        <input id="doors-range" type="range" min={0} max={20} step={1} value={state.doors} onChange={e => set('doors', Number(e.target.value))} className="mt-8 w-full cursor-pointer" data-id="element-84" data-mp="6nd476id18d" />
                        <div className="mt-3 flex justify-between text-[11px] uppercase tracking-overline text-stone-400" data-id="element-85" data-mp="3cn0lpb6k2q4p">
                          <span data-id="element-86" data-mp="3mfpwe3ua7zyz">0 puertas</span>
                          <span data-id="element-87" data-mp="3mgrayiaib4lg">20 puertas</span>
                        </div>
                      </div>}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-10 flex items-center justify-between border-t border-ink/10 pt-6" data-id="element-88" data-mp="2txatmvu6gigh">
                  <button type="button" onClick={back} disabled={step === 1} className="inline-flex items-center gap-2 text-[12.5px] tracking-wide text-stone-600 transition-colors duration-200 ease-premium hover:text-ink disabled:opacity-30" data-id="element-89" data-mp="10goa49i0kg95">
                    <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-90" data-mp="1j6nzkxhrnxd3" />
                    Atrás
                  </button>
                  <button type="button" onClick={next} className="group inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[13px] font-medium tracking-wide text-cream transition-[background-color,box-shadow] duration-200 ease-premium hover:bg-charcoal hover:shadow-lift" data-id="element-91" data-mp="1e8clgakfv0p2">
                    {step === TOTAL_STEPS ? 'Ver mi estimación' : 'Siguiente'}
                    <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-premium group-hover:translate-x-0.5" aria-hidden="true" data-id="element-92" data-mp="3pz6wfi5klohm" />
                  </button>
                </div>
              </div>
            </div> : <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid lg:grid-cols-12" data-id="element-93" data-mp="3b2gujju3jrgt">
              <div className="bg-charcoal p-8 text-cream lg:col-span-5 lg:p-10" data-id="element-94" data-mp="3152dpj49h0nz">
                <p className="text-[11px] uppercase tracking-overline text-champagne" data-id="element-95" data-mp="2n59s227ldamv">
                  Estimación orientativa
                </p>
                <p className="mt-6 font-serif text-[2.6rem] font-light leading-tight sm:text-[3.2rem]" data-id="element-96" data-mp="2s5p2akwku20m">
                  {euro(result.min)}
                  <span className="mx-2 text-cream/40" data-id="element-97" data-mp="qcrfb5phe3nl">–</span>
                  {euro(result.max)}
                </p>
                <p className="mt-3 text-[13px] text-champagne" data-id="element-98" data-mp="nym72w0bv7ir">IVA no incluido</p>
                <p className="mt-1.5 text-[12.5px] text-cream/60" data-id="element-99" data-mp="jvg3jw4lnufz">
                  Para {state.meters} m² ·{' '}
                  {state.service === 'completo' ? 'servicio completo' : 'solo material'}
                </p>

                <dl className="mt-9 space-y-4 border-t border-cream/15 pt-7" data-id="element-100" data-mp="9z3c74wj1soe">
                  {result.breakdown.map(item => <div key={item.label} className="flex items-start justify-between gap-5" data-id="element-101" data-mp="17nmzwvabs1fe">
                      <div data-id="element-102" data-mp="12ok1vusrivgx">
                        <dt className="text-[13px] text-cream" data-id="element-103" data-mp="xvh8pnrwdo19">{item.label}</dt>
                        <dd className="mt-0.5 text-[11.5px] text-cream/50" data-id="element-104" data-mp="xte7e7lao8h8">{item.detail}</dd>
                      </div>
                      <span className="shrink-0 font-serif text-lg text-champagne" data-id="element-105" data-mp="1qbry7n87qu6t">
                        {euro(item.amount)}
                      </span>
                    </div>)}
                </dl>

                <button type="button" onClick={restart} className="mt-9 inline-flex items-center gap-2 text-[12.5px] text-cream/60 transition-colors duration-200 ease-premium hover:text-cream" data-id="element-106" data-mp="1sxj6gc8y1hyf">
                  <RotateCcwIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-107" data-mp="27tqlyz9epv7d" />
                  Volver a calcular
                </button>
              </div>

              <div className="p-8 lg:col-span-7 lg:p-10" data-id="element-108" data-mp="2busopq2j8pe3">
                {result.needsTechnicalReview && <div className="flex gap-4 rounded-2xl border border-champagne/60 bg-champagne/15 p-5" data-id="element-109" data-mp="2gslskcgd57b7">
                    <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-walnut" aria-hidden="true" data-id="element-110" data-mp="2ln86ondtxdyz" />
                    <p className="text-[13px] leading-relaxed text-ink" data-id="element-111" data-mp="2qmb01akg5vr4">
                      <span className="font-medium" data-id="element-112" data-mp="2vkw3diuzwvjs">Nivelación a valorar en detalle.</span> Un
                      técnico debe revisar el estado del soporte antes de confirmar el autonivelado:
                      afecta al plazo y al acabado final.
                    </p>
                  </div>}

                <p className="mt-6 text-[12.5px] leading-relaxed text-stone-600" data-id="element-113" data-mp="3gwcgi64odq4s">
                  <span className="text-ink" data-id="element-114" data-mp="3utiy3xdnxisf">
                    Todos los importes mostrados son sin IVA; al total se le aplicará el IVA
                    correspondiente.
                  </span>{' '}
                  Esta estimación es orientativa. El precio final depende del estado real del suelo,
                  nivelación, material elegido, rodapiés, puertas, accesos y acabados. En Lussonovo
                  revisamos tu caso y te enviamos un presupuesto cerrado antes de empezar.
                </p>

                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-walnut px-7 py-4 text-[13px] font-medium tracking-wide text-cream transition-[background-color,box-shadow] duration-200 ease-premium hover:bg-[#754927] hover:shadow-lift" data-id="element-115" data-mp="372dh3evzkkgj">
                  <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-116" data-mp="yqbe1hgvc4r2" />
                  Recibir presupuesto cerrado por WhatsApp
                </a>
                <p className="mt-3 text-[12px] text-stone-600" data-id="element-117" data-mp="ozp0dssppob0">
                  Respuesta el mismo día laborable. Sin compromiso.
                </p>

                <div className="mt-10" data-id="element-118" data-mp="1hncj12afa4v0">
                  <LeadForm state={state} result={result} data-id="element-119" data-mp="1hm9vn4cb6kju" />
                </div>
              </div>
            </motion.div>}
        </div>
      </div>
    </section>;
}